import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { PromotionType } from 'generated/graphql-gateway';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { selectors, useAppSelector } from 'state/global-state';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';

import { IGetErrorMessagesProps } from './types';

export const useGetPaymentErrors = () => {
  const { cartPriceLimitExceeded, cartPriceTooLow, cartCateringPriceTooLow } = useOrderContext();
  const { checkoutPaymentMethodId } = usePaymentContext();
  const [pricedAndAvailable, setPricedAndAvailable] = useState(false);
  const formatters = useIntl();
  const incentiveEvaluationErrors = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsWithCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );

  useEffectOnUpdates(() => {
    //If payment method id changes, priced and available should be true until a new pricing is fired
    setPricedAndAvailable(true);
  }, [checkoutPaymentMethodId]);

  // This method returns an array to handle other errors in the future
  // similar to `getErrorMessages`
  const getLoyaltyPaymentErrorMessages = () => {
    if (!incentiveEvaluationErrors.length) {
      return [];
    }

    return [
      {
        messageId: '',
        message: getEvaluationResultMessage({
          evaluationResult: incentiveEvaluationErrors[0],
          formatters,
          incentiveType: PromotionType.OFFER,
        }),
      },
    ];
  };

  const getErrorMessages = useCallback(
    ({
      areDeliveryDetailsWithError,
      invalidAddressLine2,
      cartCateringPriceTooLow: serverOrderCartCateringPriceTooLow,
      cartPriceTooLow: serverOrderCartPriceTooLow,
      checkoutPaymentMethodNeedRevaultError,
      curbsideOrderError,
      invalidOfferError,
      invalidPaymentMethodError,
      invalidReloadError,
      invalidTipAmountError,
      noDirectPayAndNoPrepaidMethodError,
      pricedAndAvailableError,
      storeIsClosing,
      unableToReloadCardError,
    }: IGetErrorMessagesProps) => {
      const paymentErrors = [
        { messageId: 'curbsideOrderError', hasError: curbsideOrderError },
        { messageId: 'invalidOfferError', hasError: invalidOfferError },
        { messageId: 'invalidPaymentMethodError', hasError: invalidPaymentMethodError },
        { messageId: 'invalidTipAmountError', hasError: invalidTipAmountError },
        { messageId: 'maximumPriceError', hasError: !!cartPriceLimitExceeded },
        {
          messageId: 'minimumPriceError',
          hasError: !!cartPriceTooLow || !!serverOrderCartPriceTooLow,
        },
        {
          messageId: 'minimumPriceForCateringError',
          hasError: !!cartCateringPriceTooLow || !!serverOrderCartCateringPriceTooLow,
        },
        { messageId: 'unableToReloadCardError', hasError: unableToReloadCardError },
        {
          messageId: 'pricedAndAvailableError',
          hasError: pricedAndAvailableError && !pricedAndAvailable,
        },
        { messageId: 'areDeliveryDetailsValid', hasError: areDeliveryDetailsWithError },
        { messageId: 'invalidAddressLine2', hasError: invalidAddressLine2 },
        {
          messageId: 'noDirectPayAndNoPrepaidMethodError',
          hasError: noDirectPayAndNoPrepaidMethodError,
        },
        { messageId: 'invalidReloadError', hasError: invalidReloadError },
        {
          messageId: 'checkoutPaymentMethodNeedRevaultError',
          hasError: checkoutPaymentMethodNeedRevaultError,
        },
        { messageId: 'storeIsClosingError', hasError: storeIsClosing },
      ];
      const paymentErrorMessageIds = paymentErrors.flatMap(item =>
        item.hasError ? [{ ...item }] : []
      );
      return paymentErrorMessageIds;
    },
    [cartCateringPriceTooLow, cartPriceLimitExceeded, cartPriceTooLow, pricedAndAvailable]
  );

  return {
    getErrorMessages,
    getLoyaltyPaymentErrorMessages,
  };
};
